import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import { FormattedMessage } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { ActionsButton, BasicModal, DefaultButton, NoItemsPanel, ContainerWrapper } from 'common/components';
import { FormMessageHOC } from 'common/HOCs';
import { withTheme } from 'common/styling/theme';
import { CrossIcon, WatchIcon, CopyIcon } from 'common/icons';
import { staticStyles, getDynamicStyles } from './style';
import { getApiTokens } from '../../_redux';
import TokensForm from '../TokensForm/TokensForm';
import TokensTable from '../TokensTable';

const ApiTokens = ({ getApiTokens, apiTokens, apiTokensAreLoaded, theme, createdToken, showFormSuccessMessage }) => {
  const location = useLocation();
  const history = useHistory();

  const dynamicStyles = Object.keys(theme).length ? getDynamicStyles(theme) : ` `;
  const query = useMemo(() => ({ page_size: 10, ...queryString.parse(location.search) }), [location.search]);

  const [newToken, setNewToken] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isTokenVisible, setIsTokenVisible] = useState(false);

  useEffect(() => {
    getApiTokens({ search: query });
  }, []);

  const toggleModal = () => {
    setIsOpen(!isOpen);
  };

  const toggleTokenVisibility = () => {
    setIsTokenVisible(isVisible => !isVisible);
  };

  return (
    <ContainerWrapper>
      <div className="ApiTokens">
        <div className="ApiTokens__header">
          <FormattedMessage id="apiTokens">{txt => <h2 className="ApiTokens__caption">{txt}</h2>}</FormattedMessage>
          <DefaultButton textId="newToken" filled plus onClick={toggleModal} />
        </div>
        {newToken && createdToken?.token && (
          <div className="ApiTokens__newToken">
            <ActionsButton className="ApiTokens__newToken-closeIcon" onClickFunc={() => setNewToken(false)}>
              <CrossIcon />
            </ActionsButton>

            <FormattedMessage id="newTokenHasBeenCreated">
              {txt => <span className="ApiTokens__newToken-caption">{txt}</span>}
            </FormattedMessage>
            <div className="ApiTokens__newToken-field-wrapper">
              <div className="ApiTokens__newToken-field">
                <span className="ApiTokens__newToken-field-tokenCaption">
                  {isTokenVisible ? createdToken?.token : new Array(32).fill('*').join('')}
                </span>
                <ActionsButton className="ApiTokens__newToken-watchIcon" onClickFunc={toggleTokenVisibility}>
                  <WatchIcon />
                </ActionsButton>
              </div>
              <CopyToClipboard text={createdToken?.token} onCopy={() => showFormSuccessMessage('justCopied')}>
                <button className="ApiTokens__newToken-copyButton">
                  <CopyIcon />
                </button>
              </CopyToClipboard>
            </div>
            <FormattedMessage id="tokenSaveHint">
              {txt => <span className="ApiTokens__newToken-caption">{txt}</span>}
            </FormattedMessage>
          </div>
        )}
        {apiTokens.count > 0 ? (
          <div className="ApiTokens__table">
            <TokensTable
              data={apiTokens}
              params={{ search: query }}
              loading={!apiTokensAreLoaded}
              location={location}
              history={history}
              getList={getApiTokens}
            />
          </div>
        ) : (
          <NoItemsPanel captionSlug="noApiTokens" className="ApiTokens__noItemsPanel" />
        )}

        <BasicModal
          overlayClassName="ApiTokens__modal"
          isOpen={isOpen}
          captionId="newApiToken"
          onRequestClose={toggleModal}>
          <TokensForm
            onSubmitSuccess={() => {
              toggleModal();
              setNewToken(true);
            }}
          />
        </BasicModal>

        <style jsx global>
          {staticStyles}
        </style>
        <style jsx global>
          {dynamicStyles}
        </style>
      </div>
    </ContainerWrapper>
  );
};

ApiTokens.propTypes = {
  apiTokens: PropTypes.object.isRequired,
  getApiTokens: PropTypes.func.isRequired,
  theme: PropTypes.object,
  apiTokensAreLoaded: PropTypes.bool.isRequired,
  createdToken: PropTypes.object.isRequired,
};

ApiTokens.defaultProps = {
  theme: {},
};

export default compose(
  withTheme(),
  FormMessageHOC,
  connect(
    state => ({
      apiTokens: state.client.kyc.apiTokens,
      apiTokensAreLoaded: state.client.kyc.apiTokensAreLoaded,
      createdToken: state.client.kyc.createdToken,
    }),
    {
      getApiTokens: search => getApiTokens.request(search),
    }
  )
)(ApiTokens);
